/**
 * Partner configuration for co-branding
 *
 * Partners can customize:
 * - Logo displayed in header
 * - Brand colors (optional)
 * - Landing page messaging
 */

export interface Partner {
  id: string;
  name: string;
  logoUrl: string;
  logoAlt: string;
  /** Optional: Width of logo in pixels */
  logoWidth?: number;
  /** Optional: Primary brand color (hex) */
  primaryColor?: string;
  /** Optional: Custom welcome message */
  welcomeMessage?: string;
  /** Optional: Link to partner website */
  websiteUrl?: string;
  /** Optional: Brand name shown in co-branded header (defaults to name) */
  headerBrandName?: string;
  /** Optional: Custom subtitle on Welcome page (overrides default "Powered by {name} partnership") */
  welcomeSubtitle?: string;
  /** Optional: Co-branding theme tokens (Revenue Engine V1). */
  theme?: PartnerTheme;
  /** Optional: Default UTM/tracking context for direct visits where UTMs are absent. */
  tracking?: PartnerTracking;
  /** Optional: claimed partner context forwarded to checkout; the server establishes provenance. */
  attribution?: PartnerAttribution;
  /** Optional: Embedded-surface one-click config (gated until T-18 legal sign-off). */
  embeddedSurface?: PartnerEmbeddedSurface;
}

/** Co-branding theme tokens. */
export interface PartnerTheme {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  ctaBackgroundColor?: string;
  ctaTextColor?: string;
}

/** Default UTM/tracking context for a partner. */
export interface PartnerTracking {
  utmSource?: string;
  utmMedium?: string;
  defaultUtmCampaign?: string;
}

/** Claimed partner code forwarded to checkout; the server establishes durable provenance. */
export interface PartnerAttribution {
  partnerCode: string;
}

/** Embedded-surface one-click provisioning config (T-18 gated). */
export interface PartnerEmbeddedSurface {
  enabled: boolean;
  consentLine?: string;
  gdprController?: string;
  oneClickProvisioning?: boolean;
}

/**
 * Partner definitions
 * Add new partners here as they are onboarded
 */
export const PARTNERS: Record<string, Partner> = {
  socs: {
    id: "socs",
    name: "SOCS",
    logoUrl: "https://storage.googleapis.com/pedagogue-public/partners/socs-logo.svg",
    logoAlt: "SOCS Logo",
    logoWidth: 120,
    primaryColor: "#1E40AF",
    welcomeMessage: "Welcome to Pedagogue, powered by SOCS",
    websiteUrl: "https://socs.org.uk",
    attribution: { partnerCode: "socs" },
    tracking: { utmSource: "socs", defaultUtmCampaign: "socs-ai-standard" },
  },
  spgs: {
    id: "spgs",
    name: "SPGS",
    logoUrl: "https://storage.googleapis.com/pedagogue-public/partners/spgs-logo.png",
    logoAlt: "SPGS Logo",
    logoWidth: 280,
    primaryColor: "#7C3AED",
    welcomeMessage: "Welcome to Pedagogue, brought to you by SPGS",
    websiteUrl: "https://spgs.org",
    headerBrandName: "SOCS",
    welcomeSubtitle: "In partnership with St Paul's Girls' School London",
  },
};

/**
 * Default partner configuration (Pedagogue branding)
 */
export const DEFAULT_PARTNER: Partner = {
  id: "pedagogue",
  name: "Pedagogue",
  logoUrl: "https://storage.googleapis.com/pedagogue-public/partners/pedagogue-logo-dark.svg",
  logoAlt: "Pedagogue Logo",
  logoWidth: 160,
  primaryColor: "#10B981", // Primary green
};

/**
 * Get partner configuration by ID
 * Returns default Pedagogue branding if partner not found
 */
export function getPartnerById(partnerId: string | null): Partner {
  if (!partnerId) {
    return DEFAULT_PARTNER;
  }

  const partner = PARTNERS[partnerId.toLowerCase()];
  return partner || DEFAULT_PARTNER;
}

/**
 * Check if a partner ID is valid
 */
export function isValidPartnerId(partnerId: string | null): boolean {
  if (!partnerId) return false;
  return partnerId.toLowerCase() in PARTNERS;
}
